"use client"

import * as React from "react"
import { createContext, useContext, useEffect, useState } from "react"

type Theme = "light" | "dark" | "system"

interface ThemeProviderProps {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
  persistTheme?: boolean
}

interface ThemeContextValue {
  theme: Theme
  resolvedTheme: "light" | "dark"
  setTheme: (theme: Theme) => void
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined)

const readStoredTheme = (storageKey: string): Theme | null => {
  try {
    const value = localStorage.getItem(storageKey)
    return value === "light" || value === "dark" || value === "system" ? value : null
  } catch {
    return null
  }
}

const storeTheme = (storageKey: string, theme: Theme) => {
  try {
    localStorage.setItem(storageKey, theme)
  } catch {}
}

export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "vite-ui-theme",
  persistTheme = true,
  ...props
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(
    () => (persistTheme ? readStoredTheme(storageKey) : null) || defaultTheme
  )
  const [systemTheme, setSystemTheme] = useState<"light" | "dark">(
    () => window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
  )
  const resolvedTheme = theme === "system" ? systemTheme : theme

  useEffect(() => {
    if (theme !== "system") return
    const media = window.matchMedia("(prefers-color-scheme: dark)")
    const updateSystemTheme = () => setSystemTheme(media.matches ? "dark" : "light")
    updateSystemTheme()
    media.addEventListener("change", updateSystemTheme)
    return () => media.removeEventListener("change", updateSystemTheme)
  }, [theme])

  useEffect(() => {
    const root = window.document.documentElement
    root.classList.remove("light", "dark")
    root.classList.add(resolvedTheme)
  }, [resolvedTheme])

  const value: ThemeContextValue = {
    theme,
    resolvedTheme,
    setTheme: (newTheme: Theme) => {
      if (persistTheme) storeTheme(storageKey, newTheme)
      setThemeState(newTheme)
    },
  }


  return (
    <ThemeContext.Provider value={value} {...props}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider")
  }
  return context
}
